import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import styles from "./Login.module.css";

const USERS_URL =
  "https://1c.mf-group.com/07e10eae-630c-4115-8f43-c7e4ceb01d10/smeta_users.json";

function Login() {
  const [selectedName, setSelectedName] = useState("");
  const [password, setPassword] = useState("");

  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [usersError, setUsersError] = useState("");

  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const { login, currentUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (currentUser) navigate("/dashboard");
  }, [currentUser, navigate]);

  useEffect(() => {
    const controller = new AbortController();
    let mounted = true;

    const loadUsers = async () => {
      setUsersLoading(true);
      setUsersError("");

      try {
        const res = await fetch(USERS_URL, {
          method: "GET",
          headers: { Accept: "application/json" },
          signal: controller.signal,
          cache: "no-store",
        });

        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const data = await res.json();
        const arr = Array.isArray(data) ? data : [];

        const cleaned = arr
          .filter((x) => x && typeof x.name === "string" && x.name.trim())
          .map((x) => ({
            name: x.name.trim(),
            id: x.id || "",
          }))
          .sort((a, b) => a.name.localeCompare(b.name, "ru"));

        if (mounted) setUsers(cleaned);
      } catch {
        if (!mounted) return;
        setUsersError(
          "Не удалось загрузить список пользователей (возможно, CORS). Можно обновить страницу или временно ввести имя вручную."
        );
      } finally {
        if (mounted) setUsersLoading(false);
      }
    };

    loadUsers();

    return () => {
      mounted = false;
      controller.abort();
    };
  }, []);

  const canSubmit = useMemo(() => {
    return selectedName.trim() && password.trim() && !loading;
  }, [selectedName, password, loading]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      await login(selectedName.trim(), password);
      navigate("/dashboard");
    } catch {
      setError("Неверные учетные данные");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.loginContainer}>
      <div className={styles.loginForm}>
        <h2 className={styles.title}>Вход в систему</h2>

        {usersError && <div className={styles.error}>{usersError}</div>}
        {error && <div className={styles.error}>{error}</div>}

        <form onSubmit={handleSubmit} autoComplete="off">
          <input
            type="text"
            name="fake-user"
            autoComplete="username"
            style={{ display: "none" }}
          />

          <input
            type="password"
            name="fake-pass"
            autoComplete="current-password"
            style={{ display: "none" }}
          />

          <div className={styles.inputGroup}>
            <label className={styles.label}>Пользователь:</label>

            {users.length > 0 ? (
              <select
                className={styles.select}
                value={selectedName}
                onChange={(e) => setSelectedName(e.target.value)}
                required
                disabled={loading || usersLoading}
              >
                <option value="" disabled>
                  {usersLoading ? "Загрузка списка..." : "Выберите пользователя"}
                </option>
                {users.map((u) => (
                  <option key={u.id || u.name} value={u.name}>
                    {u.name}
                  </option>
                ))}
              </select>
            ) : (
              <input
                className={styles.input}
                type="text"
                name="login"
                value={selectedName}
                onChange={(e) => setSelectedName(e.target.value)}
                required
                disabled={loading}
                placeholder="Введите имя пользователя"
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="none"
                spellCheck={false}
              />
            )}
          </div>

          <div className={styles.inputGroup}>
            <label className={styles.label}>Пароль:</label>
            <input
              className={styles.input}
              type="password"
              name="pass"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={loading}
              autoComplete="off"
            />
          </div>

          <div className={styles.actions}>
            <button
              type="submit"
              className={styles.loginButton}
              disabled={!canSubmit}
            >
              {loading ? "Вход..." : "Войти"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default Login;